"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Clock, Wrench, Truck, FileText, Send, CircleCheck as CheckCircle2, TriangleAlert as AlertTriangle, Phone, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
export default function Cancellation() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 14
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.24,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const matrixCards = [{
    id: "std-orders",
    tag: "STANDARD CATALOG",
    title: "Standard tooling & equipment",
    cutoff: "2-hour cutoff window",
    description: "Orders for in-stock workshop tools and machinery can be cancelled at zero charge within two hours of placement before warehouse batching.",
    resolution: "Full refund issued to original payment method within 2 to 4 business days.",
    icon: Clock,
    status: "Pre-dispatch"
  }, {
    id: "custom-orders",
    tag: "CUSTOM SPEC",
    title: "Fabricated & cut-to-size items",
    cutoff: "Prior to machining",
    description: "Custom-machined hardware and cut-to-order heavy stock can be modified or cancelled only before metal cutting and fabrication cycles initiate.",
    resolution: "Subject to raw material preparation fees once fabrication setup has started.",
    icon: Wrench,
    status: "Production gate"
  }, {
    id: "transit-orders",
    tag: "IN-TRANSIT",
    title: "Dispatched freight & parcels",
    cutoff: "Post-tracking issuance",
    description: "Once a shipping tracking number has been generated and freight loaded, parcels cannot be intercepted mid-route.",
    resolution: "Transitions directly into our standard 30-day return policy upon arrival.",
    icon: Truck,
    status: "Return protocol"
  }];
  const steps = [{
    number: "01",
    title: "Locate your order ID",
    description: "Find your alphanumeric order reference in your email receipt or account order history.",
    icon: FileText
  }, {
    number: "02",
    title: "Submit cancel request",
    description: "Send a cancellation ticket via the support portal or notify our dispatch team by phone.",
    icon: Send
  }, {
    number: "03",
    title: "Receive auth confirmation",
    description: "Get immediate written confirmation with your cancellation release code and refund notice.",
    icon: CheckCircle2
  }];
  return <section id="cancellation-policy" data-nav="dark" className="bg-background text-foreground py-16 px-4 md:px-8 lg:px-12 border-b border-border">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={itemVariants} className="mb-12">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="h-2 w-2 bg-primary rounded-full" />
            <span className="font-mono text-xs md:text-sm font-semibold tracking-wider text-muted-foreground uppercase">
              Dispatch & Order Protocols
            </span>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-[3px] self-stretch bg-primary shrink-0" />
            <div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold uppercase tracking-tight text-foreground">
                Order cancellation policy
              </h2>
              <p className="mt-3 text-base md:text-lg text-muted-foreground max-w-3xl leading-relaxed">
                Review cutoff windows, fabrication criteria, and routing requirements for standard and freight workshop orders before fulfillment.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Policy Matrix Grid */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} variants={containerVariants} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {matrixCards.map((card, i) => {
          const Icon = card.icon;
          return <motion.div key={card.id} variants={itemVariants} data-index={i}>
                <Card className="h-full bg-card border-border rounded-xs flex flex-col justify-between hover:border-primary/60 transition-[border-color,box-shadow] duration-200">
                  <CardHeader className="gap-3 pb-4">
                    <div className="flex items-center justify-between gap-2">
                      <Badge variant="secondary" className="rounded-xs font-mono text-[11px] uppercase tracking-wider px-2 py-0.5 border border-border">
                        {card.tag}
                      </Badge>
                      <span className="font-mono text-xs text-primary font-semibold">
                        {card.status}
                      </span>
                    </div>

                    <div className="flex items-start gap-3 mt-2">
                      <div className="p-2.5 bg-muted rounded-xs text-primary shrink-0">
                        <Icon className="size-5" />
                      </div>
                      <div>
                        <CardTitle className="text-lg md:text-xl font-serif font-bold uppercase text-card-foreground">
                          {card.title}
                        </CardTitle>
                        <p className="font-mono text-xs text-primary mt-1 font-medium">
                          {card.cutoff}
                        </p>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4 pt-0">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {card.description}
                    </p>
                    <div className="pt-3 border-t border-border/60">
                      <p className="text-xs font-medium text-foreground">
                        <span className="font-mono text-muted-foreground uppercase mr-1">Settlement:</span>
                        {card.resolution}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Step-by-Step Cancellation Action Bar */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={itemVariants} className="mb-12 bg-card border border-border rounded-xs p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pb-4 border-b border-border">
            <div>
              <span className="font-mono text-xs text-primary uppercase font-bold tracking-wider">
                Standard Procedure
              </span>
              <h3 className="text-xl md:text-2xl font-serif font-bold uppercase text-foreground mt-1">
                How to cancel an unfulfilled order
              </h3>
            </div>
            <p className="text-xs font-mono text-muted-foreground">
              Average processing time: &lt; 30 minutes
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
            {steps.map((step, i) => {
            const StepIcon = step.icon;
            return <div key={step.number} data-index={i} className="flex flex-col bg-background/50 p-5 rounded-xs border border-border/80">
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-mono text-2xl font-bold text-primary">
                      {step.number}
                    </span>
                    <div className="p-2 bg-muted rounded-xs text-foreground">
                      <StepIcon className="size-4 text-primary" />
                    </div>
                  </div>
                  <h4 className="font-serif text-lg font-semibold uppercase text-foreground mb-2">
                    {step.title}
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </div>;
          })}
          </div>
        </motion.div>

        {/* High-Alert Notice Banner */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={itemVariants} className="mb-12 border-l-4 border-l-primary bg-card border border-border p-6 rounded-xs">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="p-2.5 bg-primary/10 rounded-xs text-primary shrink-0 mt-0.5 md:mt-0">
                <AlertTriangle className="size-5" />
              </div>
              <div>
                <h4 className="text-base font-serif font-bold uppercase text-foreground tracking-wide">
                  Urgent cutoff for same-day priority freight
                </h4>
                <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                  Priority pallet freight and express logistics are pulled from storage within 45 minutes of order confirmation. For immediate dispatch holds, contact dispatch support via phone directly.
                </p>
              </div>
            </div>

            <div className="shrink-0 flex items-center gap-2 pl-12 md:pl-0">
              <a href="tel:+14842918653" className="inline-flex items-center gap-2 font-mono text-sm font-bold text-primary bg-muted px-4 py-2 rounded-xs border border-border hover:bg-accent transition-colors">
                <Phone className="size-4" />
                <span>+1 (484) 291-8653</span>
              </a>
            </div>
          </div>
        </motion.div>

        {/* Action & Policy Footer Links */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={itemVariants} className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-border">
          <div className="text-center sm:text-left">
            <p className="text-sm text-muted-foreground">
              Need assistance with an existing order or modification?
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <Button asChild variant="secondary" className="rounded-xs uppercase font-serif font-semibold tracking-wide">
              <Link to="/ContactPage">
                <span>Submit cancellation ticket</span>
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>

            <Button asChild variant="ghost" className="rounded-xs font-mono text-xs text-primary hover:text-primary">
              <Link to="/ShippingReturnsPage">
                <span>Review complete return policy</span>
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}